<script>
import { createSimpleTextUnlayerTemplate, appendOptOutFooter } from '@/components/unlayer/unlayerEmail.util';
import { SOURCES } from '@/components/unlayer/constants/unlayer.constants';
import { DEFAULT_TEMPLATE_HTML, TEMPLATE_TYPE } from '@/components/broadcast/constants/emailBroadcast.constants.js';

import {
    deleteUnlayerEmailFunnelSavedTemplate,
    getUnlayerEmailFunnelGlobalTemplate,
    updateUnlayerEmailFunnelPublicTemplate,
    updateUnlayerEmailFunnelSavedTemplate,
    copyTemplateDependencies,
} from '@/api/campaigns/campaignUnlayerEmail';

import {
    getUnlayerBroadcastGlobalTemplate,
    getUnlayerBroadcastSavedTemplate,
    getUnlayerBroadcastTemplate,
} from '@/api/broadcasts/emailBroadcast';

const MESSAGES = {
    PREVIEW_ERROR: 'Something went wrong while previewing your broadcast. Please try again.',
    TEMPLATE_CREATE_ERROR: 'For some reason your template failed to save. Please try again.',
    CLOSE_LABEL: 'Ok, got it',
};


export default {
    props: {
        marketingEmailId: String,
        source: {
            type: String,
            default: SOURCES.CAMPAIGN,
        },
        funnelId: String,
        templateId: String,
        unlayerEmailLogoToolEnabled: Boolean,
    },

    data() {
        return {
            creatingTemplateId: null,
            deletingTemplateId: null,
            previewTemplate: null,
            previewTemplateHtml: null,
            isLoadingPreviewSelection: false,
            isLoadingPreview: false,
            previewBroadcast: null,
            allowSelection: false,
        };
    },

    computed: {
        isBroadcast() {
            return this.source === SOURCES.BROADCAST;
        },

        isCampaign() {
            return this.source === SOURCES.CAMPAIGN;
        },

        isCreating() {
            return Boolean(this.creatingTemplateId);
        },

        isDeleting() {
            return Boolean(this.deletingTemplateId);
        },
    },

    methods: {
        displayError(message) {
            const { CLOSE_LABEL } = MESSAGES;

            this.$error({
                bottom: true,
                closeLabel: CLOSE_LABEL,
                message,
                showIcon: true,
            });
        },

        selectEmailBroadcastTemplate(template = {}) {
            const emittedTemplate = {
                name: this.$t('email.broadcast.unlayer.template.untitled'),
                html: DEFAULT_TEMPLATE_HTML,
                design: {},
                title: template.title,
                ...template,
            };

            this.$emit('template-selected', emittedTemplate);
        },

        async handleFromPublicTemplateSelected(template) {
            const { isBroadcast, isCampaign } = this;

            try {
                this.creatingTemplateId = template.id;
                this.isLoadingPreviewSelection = true;

                if (isBroadcast) {
                    const globalTemplate = await getUnlayerBroadcastGlobalTemplate(template);

                    this.selectEmailBroadcastTemplate(globalTemplate);
                } else if (isCampaign) {
                    const { marketingEmailId } = this;

                    const publicTemplate = await updateUnlayerEmailFunnelPublicTemplate({ marketingEmailId, template });

                    this.$emit('template-created', {
                        design: JSON.parse(publicTemplate.builderJson.content),
                        html: publicTemplate.html,
                    });
                }
            } catch (err) {
                this.displayError(MESSAGES.TEMPLATE_CREATE_ERROR);
            } finally {
                this.creatingTemplateId = null;
                this.isLoadingPreviewSelection = false;
            }
        },

        async handleFromSavedTemplateSelected(template) {
            const { isBroadcast, isCampaign } = this;

            try {
                this.isLoadingPreviewSelection = true;
                this.creatingTemplateId = template.id;

                if (isBroadcast) {
                    const savedTemplate = await getUnlayerBroadcastSavedTemplate(template);

                    this.selectEmailBroadcastTemplate(savedTemplate);
                } else if (isCampaign) {
                    const { marketingEmailId } = this;

                    const savedTemplate = await updateUnlayerEmailFunnelSavedTemplate({ marketingEmailId, template });

                    if (this.unlayerEmailLogoToolEnabled) {
                        await copyTemplateDependencies({ templateId: this.templateId, sourceTemplateId: template.id });
                    }

                    this.$emit('template-created', { design: JSON.parse(savedTemplate.builderJson.content), html: DEFAULT_TEMPLATE_HTML });
                }
            } catch (err) {
                this.displayError(MESSAGES.TEMPLATE_CREATE_ERROR);
            } finally {
                this.creatingTemplateId = null;
                this.isLoadingPreviewSelection = false;
            }
        },

        async handleFromScratchTemplateSelected() {
            this.creatingTemplateId = 'scratch';

            const { isBroadcast, isCampaign } = this;
            const design = appendOptOutFooter();

            if (isBroadcast) {
                this.selectEmailBroadcastTemplate({ design });
            } else if (isCampaign) {
                this.$emit('template-created', { design, html: DEFAULT_TEMPLATE_HTML });
            }

            this.creatingTemplateId = null;
        },

        async handleFromSimpleTextTemplateSelected() {
            this.creatingTemplateId = 'simple-text';

            const { isBroadcast, isCampaign } = this;
            const design = appendOptOutFooter(createSimpleTextUnlayerTemplate());

            if (isBroadcast) {
                this.selectEmailBroadcastTemplate({ design });
            } else if (isCampaign) {
                this.$emit('template-created', { design, html: DEFAULT_TEMPLATE_HTML });
            }

            this.creatingTemplateId = null;
        },

        async handleUnlayerBroadcastTemplateSelected(template) {
            try {
                this.creatingTemplateId = template.id;
                const broadcastTemplate = await getUnlayerBroadcastTemplate(template);

                this.selectEmailBroadcastTemplate(broadcastTemplate);
            } catch (err) {
                this.displayError(MESSAGES.TEMPLATE_CREATE_ERROR);
            } finally {
                this.creatingTemplateId = null;
            }
        },

        async deleteSavedTemplate(template) {
            try {
                this.deletingTemplateId = template.id;

                await deleteUnlayerEmailFunnelSavedTemplate(template);

                this.$emit('item-deleted');
            } catch (error) {
                this.$emit('item-delete-error');
            } finally {
                this.deletingTemplateId = null;
            }
        },

        async handleGalleryTemplatePreview(template) {
            const { isBroadcast, isCampaign } = this;

            try {
                if (!template.id) throw new Error();

                this.isLoadingPreview = true;
                let globalTemplate;

                if (isBroadcast) {
                    globalTemplate = await getUnlayerBroadcastGlobalTemplate(template);
                } else if (isCampaign) {
                    globalTemplate = await getUnlayerEmailFunnelGlobalTemplate(template);
                }

                if (!template && globalTemplate?.html) throw new Error();

                this.previewTemplate = template;
                this.previewTemplateHtml = globalTemplate?.html;
            } catch (err) {
                this.displayError(MESSAGES.PREVIEW_ERROR);
            } finally {
                this.isLoadingPreview = false;
            }
        },

        handleTemplatePreview(template) {
            if (template.id && template.html) {
                this.previewTemplate = template;
                this.previewTemplateHtml = template.html;
                this.allowSelection = !(template.templateType === TEMPLATE_TYPE.BARD_SENT || template.templateType === TEMPLATE_TYPE.BARD_PRIVATE);
            }
            this.isPreviewing = false;
        },

        closeTemplatePreview() {
            this.previewTemplateHtml = null;
            this.previewTemplate = null;
            this.previewBroadcast = null;
        },
    },

    render() {
        const {
            handleFromPublicTemplateSelected,
            handleFromSavedTemplateSelected,
            handleFromScratchTemplateSelected,
            handleFromSimpleTextTemplateSelected,
            handleUnlayerBroadcastTemplateSelected,
            creatingTemplateId,
            deleteSavedTemplate,
            deletingTemplateId,
            handleGalleryTemplatePreview,
            handleTemplatePreview,
            closeTemplatePreview,
            previewTemplate,
            previewTemplateHtml,
            isLoadingPreviewSelection,
            isLoadingPreview,
            previewBroadcast,
            isCreating,
            isDeleting,
            allowSelection,
        } = this;

        return this.$scopedSlots.default({
            handleFromPublicTemplateSelected,
            handleFromSavedTemplateSelected,
            handleFromScratchTemplateSelected,
            handleFromSimpleTextTemplateSelected,
            handleUnlayerBroadcastTemplateSelected,
            creatingTemplateId,
            deleteSavedTemplate,
            deletingTemplateId,
            handleGalleryTemplatePreview,
            handleTemplatePreview,
            closeTemplatePreview,
            previewTemplate,
            previewTemplateHtml,
            isLoadingPreviewSelection,
            isLoadingPreview,
            previewBroadcast,
            isCreating,
            isDeleting,
            allowSelection,
        });
    },
};
</script>
